import React, { useState, useRef, useCallback, Fragment, useMemo } from 'react';

import classNames from 'classnames';

import UploadProgressCircle from './UploadProgressCircle';

import LoadingSpinner from 'CommonComponents/LoadingSpinner';

import CircleInfo from 'Assets/icons/circle-info.svg';
import FilePlusIcon from 'Assets/icons/file-plus.svg';

const FileUploadBox = ({
    onFilesSelected,
    accept = '*',
    multiple = true,
    maxSize = 10 * 1024 * 1024, // 10MB default
    maxFiles = 5,
    remainingSlots = 5,
    uploading = false,
    uploadProgress = {}, // { label: <string>, progress: <int> }
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const dragCounter = useRef(0);
    const dropZoneRef = useRef(null);

    const isDisabled = useMemo(() => uploading || remainingSlots === 0, [uploading, remainingSlots]);

    const validateFile = useCallback(
        (file) => {
            if (file.size > maxSize) {
                return $t('The document "{{fileName}}" you are trying to upload exceeds the document limit of {{humanReadableMaxSize}}', {
                    fileName: file.name,
                    humanReadableMaxSize: formatBytes(maxSize),
                });
            }

            if (accept !== '*') {
                const acceptedTypes = accept.split(',').map((type) => type.trim());
                const fileType = file.type;
                const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;

                const isAccepted = acceptedTypes.some((type) => {
                    if (type.startsWith('.')) {
                        return fileExtension === type.toLowerCase();
                    }
                    if (type.endsWith('/*')) {
                        return fileType.startsWith(type.replace('/*', '/'));
                    }
                    return fileType === type;
                });

                if (!isAccepted) {
                    return $t('File "{{fileName}}" has an unsupported format {{fileExtension}}', { fileName: file.name, fileExtension });
                }
            }

            return null;
        },
        [accept, maxSize],
    );

    const processFiles = useCallback(
        (fileList) => {
            if (fileList.length === 0) return;

            setError(null);
            const filesArray = Array.from(fileList);

            if (!multiple && filesArray.length > 1) {
                setError($t('Only one file can be uploaded at a time'));
                return;
            }

            if (filesArray.length > maxFiles) {
                setError($t('Maximum {{maxFiles}} files allowed', { maxFiles }));
                return;
            }

            const validFiles = [];
            const errors = [];

            filesArray.forEach((file) => {
                const validationError = validateFile(file);
                if (validationError) {
                    errors.push(validationError);
                } else {
                    validFiles.push({
                        file,
                        id: `${file.name}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                        progress: 0,
                        status: 'pending',
                    });
                }
            });

            // validate remaining slots
            if (validFiles.length > remainingSlots) {
                errors.push(
                    $t('You can only upload {{remainingSlots}} more document(s) - Please delete another document to upload new documents', { remainingSlots }),
                );
            }

            if (errors.length > 0) {
                setError(errors.join('. '));
                return;
            }

            if (validFiles.length > 0) {
                const promise = onFilesSelected(validFiles.map((f) => f.file));

                if (promise instanceof Promise) {
                    promise.then(() => {
                        if (fileInputRef.current) {
                            fileInputRef.current.value = '';
                        }
                    });
                }
            }
        },
        [maxFiles, multiple, onFilesSelected, validateFile, remainingSlots],
    );

    const handleDragEnter = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (isDisabled) {
                return;
            }

            dragCounter.current++;
            if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
                setIsDragging(true);
            }
        },
        [isDisabled],
    );

    const handleDragLeave = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (isDisabled) {
                return;
            }

            // Check if we're actually leaving the drop zone (not just entering a child element)
            // relatedTarget is the element we're entering - if it's outside our dropzone, we're leaving
            const relatedTarget = e.relatedTarget;

            if (!relatedTarget || !dropZoneRef.current?.contains(relatedTarget)) {
                setIsDragging(false);
            }
        },
        [isDisabled],
    );

    const handleDragOver = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (isDisabled) {
                return;
            }

            if (!isDragging) {
                setIsDragging(true);
            }
        },
        [isDragging, isDisabled],
    );

    const handleDrop = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (isDisabled) {
                return;
            }

            setIsDragging(false);
            dragCounter.current = 0;

            const { files: droppedFiles } = e.dataTransfer;
            if (droppedFiles && droppedFiles.length > 0) {
                processFiles(droppedFiles);
            }
        },
        [processFiles, isDisabled],
    );

    const handleDragEnd = useCallback(
        (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (isDisabled) {
                return;
            }

            setIsDragging(false);
        },
        [isDisabled],
    );

    const handleFileInputChange = useCallback(
        (e) => {
            const { files: selectedFiles } = e.target;
            if (selectedFiles && selectedFiles.length > 0) {
                processFiles(selectedFiles);
            }
            // Reset input value to allow selecting the same file again
            e.target.value = '';
        },
        [processFiles],
    );

    const handleClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    return (
        <div className="w-full max-w-2xl mx-auto">
            <div
                ref={dropZoneRef}
                role="button"
                tabIndex={0}
                className={classNames(
                    'tw-relative tw-h-48 tw-flex tw-items-center tw-justify-center tw-bg-gray-50 tw-border-2 tw-border-dashed tw-rounded-lg tw-p-6 tw-transition-colors hover:tw-bg-gray-100',
                    isDragging ? 'tw-border-blue-500 tw-bg-blue-50' : 'tw-border-gray-300',
                    isDisabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer',
                    {
                        'tw-opacity-50': isDisabled && !uploading,
                    },
                )}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onDragEnd={handleDragEnd}
                onClick={handleClick}
            >
                {isDragging && <div className="tw-absolute tw-inset-0 tw-z-10" />}
                <div className="tw-text-center tw-pointer-events-none">
                    {!uploading && (
                        <Fragment>
                            <FilePlusIcon className={classNames('tw-mx-auto tw-h-12 tw-w-12 tw-mb-4', isDragging ? 'tw-text-blue-500' : 'tw-text-gray-400')} />
                            {isDragging && remainingSlots > 0 ? (
                                <div className="tw-mt-4">
                                    <p className="tw-text-blue-600 tw-font-medium">{$t('Drop files here to upload')}</p>
                                </div>
                            ) : (
                                <>
                                    <div className="tw-mt-4">
                                        <label className={classNames(isDisabled ? 'tw-cursor-not-allowed' : 'tw-cursor-pointer')}>
                                            <span className="tw-text-blue-75 hover:tw-text-blue-50 tw-font-medium">{$t('Upload files')}</span>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                className="tw-sr-only"
                                                onChange={handleFileInputChange}
                                                accept={accept}
                                                disabled={isDisabled}
                                            />
                                        </label>
                                        <span className="tw-text-gray-500"> {$t('or drag and drop')}</span>
                                    </div>
                                    <p className="tw-text-xs tw-text-gray-500 tw-mt-2">
                                        {$t('PDF, Excel, Word up to {{humanReadableMaxSize}}', { humanReadableMaxSize: formatBytes(maxSize) })}
                                    </p>
                                    {remainingSlots > 0 ? (
                                        <p className="tw-text-xs tw-text-gray-500">
                                            {$t('{{remainingSlots}} of {{maxFiles}} slots remaining', { remainingSlots, maxFiles })}
                                        </p>
                                    ) : (
                                        <p className="tw-text-xs tw-text-red-75 tw-max-w-xs">
                                            {$t(
                                                `Limit of {{maxFiles}} uploaded documents has been reached, please delete a document to upload further documents`,
                                                { maxFiles },
                                            )}
                                        </p>
                                    )}
                                </>
                            )}
                        </Fragment>
                    )}
                    {uploading && (
                        <div className="tw-mt-4 tw-flex tw-items-center tw-justify-center tw-gap-2">
                            <LoadingSpinner size="sm" />
                            <span className="tw-text-sm tw-text-gray-600">{$t('Uploading...')}</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Upload Progress List */}
            {uploadProgress?.progress !== undefined && (
                <div className="tw-mt-4 tw-space-y-2">
                    <div className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-bg-gray-50 tw-rounded-lg tw-border tw-border-gray-200">
                        <UploadProgressCircle progress={uploadProgress.progress} size={40} strokeWidth={3} />
                        <div className="tw-flex-1 tw-min-w-0">
                            <p className="tw-text-sm tw-font-medium tw-text-gray-700 tw-truncate">{uploadProgress.label}</p>
                            <p className="tw-text-xs tw-text-gray-500">{uploadProgress.progress === 100 ? $t('Processing...') : $t('Uploading...')}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="tw-mt-4 tw-p-3 tw-bg-red-25/40 tw-border tw-border-red-50 tw-rounded-lg">
                    <p className="tw-text-sm tw-text-red-600 tw-flex tw-items-center tw-gap-2">
                        <CircleInfo className="tw-w-4 tw-h-4 tw-flex-shrink-0" />
                        {error}
                    </p>
                </div>
            )}
        </div>
    );
};

// Utility function
const formatBytes = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

export default FileUploadBox;
